<template>
    <DCard padding>
        <DTable
            class="table-responsive"
            :items="items"
            :fields="getFields"
        >
            <template #contract_id="{item}">
                <td>
                    <Link
                        v-if="item.contract_id && userPermissions.includes('contract.view') === true"
                        :href="contractRoute(item)"
                    >
                        {{ item.contract_id }}
                    </Link>
                    <span v-if="item.contract_id && userPermissions.includes('contract.view') === false">{{ item.contract_id }}</span>
                </td>
            </template>
            <template #product_id="{item}">
                <td>
                    <Link
                        v-if="item.product_id && userPermissions.includes('product.view') === true"
                        :href="productRoute(item)"
                    >
                        {{ item.product_id }}
                    </Link>
                    <span v-if="item.product_id && userPermissions.includes('product.view') === false">{{ item.product_id }}</span>
                </td>
            </template>
            <template #tax_rate="{item}">
                <td>{{ taxRate(item.tax_rate) }}</td>
            </template>
            <template #price="{item}">
                <td>{{ roundPrice(item.price) }}</td>
            </template>
            <template #net_amount="{item}">
                <td>{{ roundPrice(item.net_amount) }}</td>
            </template>
            <template #from_date="{item}">
                <td>{{ setDateFormatEuropeBerlin(item.from_date) }}</td>
            </template>
            <template #to_date="{item}">
                <td>{{ setDateFormatEuropeBerlin(item.to_date) }}</td>
            </template>
            <template #quantity="{item}">
                <td>{{ showQuantity(item.quantity) }}</td>
            </template>
            <template #actions="{item}">
                <td>
                    <DButton
                        :id="`btn-delete-${item.id}`"
                        :disabled="userPermissions.includes('invoice.manage') === false"
                        mini
                        icon="trash-can-outline"
                        color="danger"
                        @click="destroyItem(item.id)"
                    />
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="pendingInvoiceItems.last_page > 1"
            :active-page="pendingInvoiceItems.current_page"
            :pages="pendingInvoiceItems.last_page"
            align="center"
            @update:activePage="changePage"
        />
        <DModal
            v-model="activityLogComment"
            v-model:show="showConfirmationModal"
            :title="__('customers.invoices.pending_invoice_items.modals.destroy_confirmation_title')"
            :message="__('customers.invoices.pending_invoice_items.modals.destroy_confirmation_message')"
            is-confirm
            is-error
            :show-change-reason-input="true"
            @close="closeConfirmationModal"
            @confirm="deleteCallBack"
        />
    </DCard>
</template>

<script>
import App from '~@/App.vue'
import CustomerBase from '~@/Pages/Customers/Base'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import priceFormat from '~@/Composables/priceFormat'

export default {
    layout: [App, CustomerBase]
}
</script>
<script setup>

const props = defineProps({
    pendingInvoiceItems: {
        type: Object,
        default: () => {
            return {}
        }
    },
    items: {
        type: Object,
        default: () => {
            return {}
        }
    },
    customer: {
        type: Object,
        default: () => { return {} }
    }
})

const userPermissions = computed(() => usePage().props.auth.permissions)
const getFields = [
    { key: 'contract_id', label: __('customers.invoices.pending_invoice_items.contract_id') },
    { key: 'product_id', label: __('customers.invoices.pending_invoice_items.product_id') },
    { key: 'line_item', label: __('customers.invoices.pending_invoice_items.line_item') },
    { key: 'description', label: __('customers.invoices.pending_invoice_items.description') },
    { key: 'tax_rate', label: __('customers.invoices.pending_invoice_items.tax_rate') },
    { key: 'from_date', label: __('customers.invoices.pending_invoice_items.from_date') },
    { key: 'to_date', label: __('customers.invoices.pending_invoice_items.to_date') },
    { key: 'price', label: __('customers.invoices.pending_invoice_items.price') },
    { key: 'quantity', label: __('customers.invoices.pending_invoice_items.quantity') },
    { key: 'net_amount', label: __('customers.invoices.pending_invoice_items.net_amount') },
    { key: 'actions', label: __('customers.invoices.pending_invoice_items.actions') }
]

const showConfirmationModal = ref(false)
const activityLogComment = ref('')

let deleteCallBack = () => { return {} }

function changePage (page) {
    const query = {}

    if (page > 1) {
        query.page = page
    }

    router.get(route('customers.customers.invoices.pending-invoice-items', { customer: props.customer.id }), query)
}

function contractRoute (item) {
    return route('customers.customers.contracts.show', { customer: item.customer_id, contract: item.contract_id })
}

function productRoute (item) {
    return route('products.products.show', { product: item.product_id })
}

function roundPrice (value) {
    if (value || value === 0.0) {
        return priceFormat.priceFormat(value, usePage().props.marketRegions[props.customer.market_region_id]?.decimal_places ?? 2, props.customer.market_region_id)
    }

    return ''
}

function showQuantity (value) {
    if (value) {
        return value
    }

    return ''
}

function taxRate (value) {
    if (value) {
        return priceFormat.pricePercentage(value, 2)
    }

    return ''
}

function destroyItem (id) {
    deleteCallBack = () => {
        router.delete(route('customers.customers.invoices.pending-invoice-items.destroy', {
            customer: props.customer.id,
            pending_invoice_item: id,
            activity_log_comment: activityLogComment.value
        }))
        closeConfirmationModal()
    }

    showConfirmationModal.value = true
}

function closeConfirmationModal () {
    activityLogComment.value = ''
    showConfirmationModal.value = false
}

</script>
